import React, { useCallback, useMemo, useContext } from "react";
import PropTypes from "prop-types";

import SideSheet from "@acadia-ui/sidesheet";
import Button, { BUTTON_DISPLAY } from "@acadia-ui/button";
import { ToolbarContext } from "@acadia-ui/toolbar";

import usePlatformDropdown from "./usePlatformDropdown";
import useDatePicker from "./useDatePicker";
import useKeyValueFilters from "./useKeyValueFilters";
import useDraftStatusDropdown from "./useDraftStatusDropdown";
import { inferDateRangeFromFilterPresets } from "../utils";

export const FiltersSidesheet = ({ isOpen, onClose, onSave, availablePlatforms, prefix, hasDraft }) => {
	const { filterValues, setFilterValues } = useContext(ToolbarContext);

	const { element: platformDropdown, value: platformValue, setValue: setPlatformValue } = usePlatformDropdown({ isOpen, availablePlatforms, prefix });
	const { element: datePicker, value: datePickerValue, setValue: setDatePickerValue } = useDatePicker({ isOpen, prefix });
	const { element: keyValueFilters, value: keyValueFilterValues, clearValues: clearKeyValueFilterValues } = useKeyValueFilters({ isOpen, prefix });
	const { element: draftStatusDropdown, value: draftStatusValue, setValue: setDraftStatusValue } = useDraftStatusDropdown({ isOpen, prefix });

	const elements = [datePicker, platformDropdown, keyValueFilters, ...(hasDraft ? [draftStatusDropdown] : [])];

	const handleClearAll = useCallback(() => {
		setPlatformValue([]);
		setDatePickerValue([]);
		clearKeyValueFilterValues();
		setDraftStatusValue([]);
	}, [setPlatformValue, setDatePickerValue, clearKeyValueFilterValues, setDraftStatusValue]);

	const unsavedFilterValue = useMemo(() => {
		const newFilters = { ...filterValues };
		if (datePickerValue?.filter((v) => v)?.length > 0) {
			newFilters[`${prefix}-filter-date`] = { "custom-range": datePickerValue };
		} else {
			delete newFilters[`${prefix}-filter-date`];
		}

		newFilters[`${prefix}-filter-platform-name`] = platformValue.reduce((acc, { value }) => ({ ...acc, [value]: true }), {});
		newFilters[`${prefix}-filter-keyValueFilter`] = keyValueFilterValues;

		// Draft status filter
		if (hasDraft) {
			newFilters[`${prefix}-filter-draftStatus`] = draftStatusValue.reduce((acc, { value }) => ({ ...acc, [value]: true }), {});
			if (!Object.values(newFilters[`${prefix}-filter-draftStatus`]).some((value) => value)) {
				delete newFilters[`${prefix}-filter-draftStatus`];
			}
		}

		// if platform does not contain any true values, remove the key from the object.
		if (!Object.values(newFilters[`${prefix}-filter-platform-name`]).some((value) => value)) {
			delete newFilters[`${prefix}-filter-platform-name`];
		}

		// if kvf does not contain any true values, remove the key from the object.
		if (!Object.values(newFilters[`${prefix}-filter-keyValueFilter`]).some((value) => value)) {
			delete newFilters[`${prefix}-filter-keyValueFilter`];
		}

		return newFilters;
	}, [filterValues, datePickerValue, platformValue, keyValueFilterValues, draftStatusValue, prefix, hasDraft]);

	const handleOnSave = useCallback(() => {
		setFilterValues(unsavedFilterValue);
		onSave(unsavedFilterValue);
		onClose();
	}, [unsavedFilterValue, onSave, onClose, setFilterValues]);

	const applyFilterDisabled = useMemo(() => {
		if (!isOpen) {
			return false;
		}

		if (Object.keys(filterValues ?? {})?.length === 0 && Object.keys(unsavedFilterValue ?? {})?.length === 0) {
			return true;
		}

		const filterValuesExcludingDate = { ...filterValues };
		const unsavedFilterValueExcludingDate = { ...unsavedFilterValue };

		delete filterValuesExcludingDate[`${prefix}-filter-date`];
		delete unsavedFilterValueExcludingDate[`${prefix}-filter-date`];

		const stateObjectsAreEqual = JSON.stringify(filterValuesExcludingDate) === JSON.stringify(unsavedFilterValueExcludingDate);

		const [unsavedStartDate, unsavedEndDate] = unsavedFilterValue?.[`${prefix}-filter-date`]?.["custom-range"] || [];
		const [newStartDate, newEndDate] = inferDateRangeFromFilterPresets(filterValues?.[`${prefix}-filter-date`]);

		// prettier-ignore
		return stateObjectsAreEqual && (
			unsavedStartDate?.toDateString?.() === newStartDate?.toDateString?.() && 
			unsavedEndDate?.toDateString?.() === newEndDate?.toDateString?.()
		);
	}, [filterValues, unsavedFilterValue, isOpen, prefix]);

	const footerActions = useMemo(
		() => (
			<>
				<Button
					// this comment exists to prevent eslint from making this unreadable.
					display={BUTTON_DISPLAY.SECONDARY}
					onClick={handleClearAll}
					disabled={Object.keys(unsavedFilterValue ?? {})?.length === 0}
					data-tracking-id-alt="clear-all-filters"
				>
					Clear All
				</Button>
				<Button
					// this comment exists to prevent eslint from making this unreadable.
					onClick={handleOnSave}
					disabled={applyFilterDisabled}
					data-tracking-id-alt="apply-filters"
				>
					Done
				</Button>
			</>
		),
		[handleClearAll, handleOnSave, applyFilterDisabled, unsavedFilterValue]
	);

	return (
		<SideSheet
			// This comment exists to prevent eslint from making this unreadable.
			isOpen={isOpen}
			onClose={onClose}
			headline={"Filters"}
			showClose={true}
			actionSetProps={{ actionsRight: footerActions }}
		>
			{elements?.map((element, index) => (
				<div key={index}>{element}</div>
			))}
		</SideSheet>
	);
};

FiltersSidesheet.propTypes = {
	isOpen: PropTypes.bool,
	onClose: PropTypes.func,
	onSave: PropTypes.func.isRequired,
	availablePlatforms: PropTypes.array.isRequired,
	prefix: PropTypes.string.isRequired,
	hasDraft: PropTypes.bool
};
